import { Dispatch, SetStateAction, useState } from 'react';
import * as R from 'ramda';

import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import { Checkbox, Collapse, Icon, List } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon } from '@atom/mui/icons';
import { UserGroupTreeType } from '@atom/types/userGroups';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import classes from './policyUserGroupSelection.module.css';

const { ListItemButton, ListItemText } = List;

interface Props {
  userGroup: UserGroupTreeType;
  selected: Set<string>;
  onToggle: (userGroup: UserGroupTreeType) => void;
  handleToggleDescendants: (
    userGroup: UserGroupTreeType,
    selectAll: boolean,
  ) => void;
  level: number;
  open: Set<string>;
  setOpen: Dispatch<SetStateAction<Set<string>>>;
}

const getListItemStyle = (hasChildren: boolean, level: number) =>
  hasChildren
    ? { paddingLeft: `${level}rem`, paddingRight: '1rem' }
    : { paddingLeft: `${level + 2}rem`, paddingRight: '1rem' };

const styles = {
  toggle: {
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
};

const UserGroupTreeNode = ({
  userGroup,
  selected,
  onToggle,
  handleToggleDescendants,
  level,
  open,
  setOpen,
}: Props) => {
  const [hoverId, setHoverId] = useState<string>();
  const [selectAll, setSelectAll] = useState<boolean>(true);

  const handleToggleSetDescendent = () => {
    handleToggleDescendants(userGroup, selectAll);
    setSelectAll(!selectAll);
  };

  const hasChildren = !!userGroup?.userGroups?.length;
  const hovering = hoverId === userGroup?.id;
  const showDescendantToggle = hovering && hasChildren;

  return (
    <div key={userGroup?.id}>
      <ListItemButton
        disableGutters
        style={getListItemStyle(hasChildren, level)}
        onMouseEnter={() => setHoverId(userGroup?.id)}
        onMouseLeave={() => setHoverId(null)}
      >
        {hasChildren && (
          <Icon
            style={styles.toggle}
            onClick={() => setOpen(toggleFromSet(open, userGroup.id))}
            icon={open.has(userGroup?.id) ? ArrowDropDownIcon : ArrowRightIcon}
          />
        )}
        <Checkbox
          checked={selected.has(userGroup?.id)}
          onClick={event => {
            event.stopPropagation();
            onToggle(userGroup);
          }}
        />
        <div style={{ marginRight: '1rem' }}>
          <UserGroupIcon colorId={userGroup?.colorId} />
        </div>
        <ListItemText primary={userGroup?.name} />
        {showDescendantToggle && (
          <div
            className={classes.toggleButton}
            onClick={handleToggleSetDescendent}
          >
            {`${selectAll ? 'Select' : 'Unselect'} All Sub Groups`}
          </div>
        )}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open.has(userGroup?.id)} timeout="auto" unmountOnExit>
          {userGroup?.userGroups.map(userGroupItem => (
            <UserGroupTreeNode
              userGroup={userGroupItem}
              selected={selected}
              onToggle={onToggle}
              handleToggleDescendants={handleToggleDescendants}
              open={open}
              setOpen={setOpen}
              level={R.inc(level)}
            />
          ))}
        </Collapse>
      )}
    </div>
  );
};

export default UserGroupTreeNode;
